// FloatingHud — generic position-persistent floating panel.
//
// Renders a `position: fixed` container with a drag handle on top, a
// fold button, optional close button, and a body that may be collapsed.
// Position is persisted by id in localStorage via useFloatingHudPosition.
//
// Layout note: top-level container uses `pointer-events: none` so the
// HUD's surrounding area doesn't intercept canvas clicks; only the panel
// itself takes pointer events.

import { useRef, type ReactNode } from 'react';
import { GripHorizontal, ChevronUp, ChevronDown, RotateCcw } from 'lucide-react';
import { useFloatingHudPosition, type FloatingHudPosition } from '@/hooks/useFloatingHudPosition';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface FloatingHudProps {
  /** Stable id — used as the localStorage key. */
  id: string;
  /** Initial position the first time this HUD is mounted. */
  defaultPosition: FloatingHudPosition;
  /** Title shown next to the drag grip. */
  title?: string;
  /** Body content — only visible when not folded. */
  children: ReactNode;
  /** Tailwind class overrides for the outer panel. */
  className?: string;
  /** Approximate dimensions for viewport-clamp math. Inexact is fine. */
  approxSize?: { width: number; height: number };
  /** Render a "reset to default position" button in the title bar. */
  showResetButton?: boolean;
}

export function FloatingHud({
  id,
  defaultPosition,
  title,
  children,
  className,
  approxSize,
  showResetButton = false,
}: FloatingHudProps) {
  const { position, folded, setFolded, dragHandlers, reset } = useFloatingHudPosition({
    id,
    defaultPosition,
    approxSize,
  });
  const panelRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={panelRef}
      className={cn(
        'fixed z-40 pointer-events-auto select-none',
        'bg-card/95 backdrop-blur-sm border border-border rounded-lg shadow-lg',
        className,
      )}
      style={{ left: position.x, top: position.y }}
      role="region"
      aria-label={title ?? 'Floating panel'}
    >
      <div
        {...dragHandlers}
        className={cn(
          'flex items-center gap-1.5 px-2 py-1 cursor-move touch-none',
          'border-b border-border bg-muted/50 rounded-t-lg',
          folded && 'border-b-0 rounded-b-lg',
        )}
        aria-label="Drag handle"
      >
        <GripHorizontal className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
        {title && (
          <span className="text-[11px] font-medium text-muted-foreground truncate">{title}</span>
        )}
        <div className="ml-auto flex items-center gap-0.5">
          {showResetButton && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 p-0"
              onClick={reset}
              title="Återställ position"
              aria-label="Återställ position"
            >
              <RotateCcw className="w-3 h-3" />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 p-0"
            onClick={() => setFolded(!folded)}
            title={folded ? 'Vik ut' : 'Vik ihop'}
            aria-label={folded ? 'Vik ut' : 'Vik ihop'}
            aria-expanded={!folded}
          >
            {folded ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
          </Button>
        </div>
      </div>
      {!folded && (
        <div className="p-2">
          {children}
        </div>
      )}
    </div>
  );
}
